import type { User } from '@supabase/supabase-js';
import { Eye, EyeOff, KeyRound, UserCircle } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { cn } from '../lib/utils';
import { formatDateTime } from '../utils/dates';

interface AccountSectionProps {
  user: User | undefined;
}

/* ── Account Section ── */
function AccountSection({ user }: AccountSectionProps) {
  const initial = user?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="bg-surface border border-border rounded-lg p-8 mb-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 font-serif text-[1.05rem] font-semibold text-ink tracking-tight">
          <UserCircle size={18} className="text-accent flex-shrink-0" /> Información de la Cuenta
        </div>
      </div>

      <div className="flex items-start gap-8 flex-wrap">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-full bg-accent-subtle border border-accent text-accent font-serif text-[1.5rem] font-bold flex items-center justify-center flex-shrink-0">
          {initial}
        </div>

        {/* Account info grid */}
        <div className="grid grid-cols-2 gap-5 flex-1 max-sm:grid-cols-1">
          <div className="flex flex-col gap-1">
            <span className="text-[0.75rem] font-semibold text-ink-tertiary uppercase tracking-[0.05em]">
              Email
            </span>
            <span className="text-[0.875rem] text-ink-secondary leading-snug">
              {user?.email ?? '—'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[0.75rem] font-semibold text-ink-tertiary uppercase tracking-[0.05em]">
              Último acceso
            </span>
            <span className="text-[0.875rem] text-ink-secondary leading-snug">
              {formatDateTime(user?.last_sign_in_at)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[0.75rem] font-semibold text-ink-tertiary uppercase tracking-[0.05em]">
              Cuenta creada
            </span>
            <span className="text-[0.875rem] text-ink-secondary leading-snug">
              {formatDateTime(user?.created_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Password Section ── */
function PasswordSection() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: '' | 'error' | 'success' }>({
    text: '',
    type: '',
  });

  // El enlace de recuperación aterriza en /perfil?recovery=1
  const [searchParams, setSearchParams] = useSearchParams();
  const isRecovery = searchParams.get('recovery') === '1';
  const cardRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo interesa al montar en modo recovery
  useEffect(() => {
    if (isRecovery) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  function clearRecoveryParam() {
    if (!isRecovery) return;
    const next = new URLSearchParams(searchParams);
    next.delete('recovery');
    setSearchParams(next, { replace: true });
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (newPassword.length < 6) {
      setMessage({ text: 'La contraseña debe tener al menos 6 caracteres', type: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Las contraseñas no coinciden', type: 'error' });
      return;
    }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSaving(false);

    if (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    } else {
      setMessage({ text: 'Contraseña actualizada correctamente', type: 'success' });
      setNewPassword('');
      setConfirmPassword('');
      clearRecoveryParam();
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150';

  return (
    <div ref={cardRef} className="bg-surface border border-border rounded-lg p-8 mb-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 font-serif text-[1.05rem] font-semibold text-ink tracking-tight">
          <KeyRound size={18} className="text-accent flex-shrink-0" /> Cambiar Contraseña
        </div>
      </div>

      {isRecovery && (
        <div className="mb-6 px-4 py-3 bg-accent-subtle border-l-[3px] border-accent rounded-r-sm">
          <p className="text-[0.875rem] text-ink-secondary leading-snug">
            Define tu nueva contraseña para recuperar el acceso a tu cuenta.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5 max-w-[420px]">
        <div>
          <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
            Nueva contraseña
          </label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              className={cn(inputClass, 'pr-10')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-sm text-ink-tertiary hover:text-ink hover:bg-nav-hover transition-[color,background-color] duration-150 inline-flex items-center"
              onClick={() => setShowNew((v) => !v)}
              tabIndex={-1}
            >
              {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <span className="block mt-2 text-[0.8125rem] text-ink-tertiary leading-snug">
            Mínimo 6 caracteres
          </span>
        </div>

        <div>
          <label className="block mb-2 font-medium text-[0.8125rem] text-ink-secondary tracking-wide">
            Confirmar contraseña
          </label>
          <input
            type="password"
            className={inputClass}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
        </div>

        <div className="flex items-center gap-5 pt-2">
          <button
            type="submit"
            className="px-6 py-3 bg-accent text-ink-inverse border-none rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer transition-[background-color,box-shadow] duration-150 hover:bg-accent-hover hover:shadow-[0_0_16px_rgba(200,150,62,0.2)] disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={saving || !newPassword || !confirmPassword}
          >
            <KeyRound size={14} /> {saving ? 'Actualizando...' : 'Actualizar contraseña'}
          </button>

          {message.text && (
            <span
              className={cn(
                'text-[0.875rem] font-medium',
                message.type === 'error' ? 'text-destructive' : 'text-success'
              )}
            >
              {message.text}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/* ── Profile (main) ── */
function Profile() {
  const { session } = useAuth();

  return (
    <div>
      <div className="mb-8">
        <h3 className="font-serif text-[1.35rem] font-semibold text-ink tracking-tight">Perfil</h3>
        <p className="text-ink-tertiary text-[0.875rem] mt-2 leading-snug">
          Información de tu cuenta y seguridad
        </p>
      </div>

      <AccountSection user={session?.user} />
      <PasswordSection />
    </div>
  );
}

export default Profile;
